import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import PreviewIcon from '@mui/icons-material/Preview';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import trainingApi from '../../services/trainingApi';
import { useTraining } from '../../context/TrainingContext';
import { getPlatformSettings } from '../../services/api';
import ScenarioPreviewDialog from '../../components/training/ScenarioPreviewDialog';
import VoiceSelector from '../../components/training/VoiceSelector';
import GeminiVoiceSelector from '../../components/common/VoiceSelector';
import AccentSelector from '../../components/common/AccentSelector';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditIcon from '@mui/icons-material/Edit';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import PersonaPickerModal from '../../components/admin/personaLibrary/PersonaPickerModal';
import { customiseScenarioPersona } from '../../services/personaLibraryApi';
import PersonaEditDialog from '../../components/training/PersonaEditDialog';
import PatientSituationPanel from '../../components/training/PatientSituationPanel';

/**
 * Patient image section — surfaces the AI-generated persona portrait
 * with Generate / Regenerate / Remove controls. Only available once the
 * scenario has been saved (we need a stable scenarioId for the route).
 */
const PersonaImageSection = ({ scenario, isEditMode, onImageChange, setError }) => {
  const [generating, setGenerating] = React.useState(false);
  const persona = typeof scenario.clientPersonaId === 'object' ? scenario.clientPersonaId : null;
  const personaImage = persona?.personaImage || null;
  const hasImage = !!personaImage?.url;

  if (!isEditMode) {
    return (
      <Alert severity="info">
        Save the scenario first, then you'll be able to generate an AI portrait of the patient.
      </Alert>
    );
  }

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const response = await trainingApi.generatePersonaImage(scenario._id);
      const body = response.data || response;
      const data = body.data || body;
      onImageChange(data.personaImage || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to generate persona image');
    } finally {
      setGenerating(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the persona portrait? You can regenerate it any time.')) return;
    setGenerating(true);
    setError(null);
    try {
      await trainingApi.deletePersonaImage(scenario._id);
      onImageChange({ url: null, prompt: null, provider: null, model: null, generatedAt: null, generatedBy: null });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to remove persona image');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Image / placeholder */}
        <Box
          sx={{
            width: 140,
            height: 140,
            borderRadius: 1,
            bgcolor: 'grey.200',
            backgroundImage: hasImage ? `url(${personaImage.url})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: 12,
          }}
        >
          {!hasImage && (generating ? <CircularProgress size={24} /> : 'No portrait')}
        </Box>

        {/* Controls + meta */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Patient image</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            AI-generated portrait of the patient persona. Shown to therapists during practice for added realism.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={hasImage ? <RefreshIcon /> : <AutoAwesomeIcon />}
              disabled={generating}
              onClick={handleGenerate}
            >
              {generating ? 'Generating…' : (hasImage ? 'Regenerate' : 'Generate image')}
            </Button>
            {hasImage && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={generating}
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </Box>

          {hasImage && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Model: <strong>{personaImage.model || 'unknown'}</strong>
                {personaImage.generatedAt && ` · ${new Date(personaImage.generatedAt).toLocaleString()}`}
              </Typography>
              {personaImage.prompt && (
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, fontStyle: 'italic',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                  Prompt: {personaImage.prompt}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const CATEGORIES = [
  { value: 'mood_disorders', label: 'Mood Disorders' },
  { value: 'anxiety_disorders', label: 'Anxiety Disorders' },
  { value: 'psychotic_disorders', label: 'Psychotic Disorders' },
  { value: 'personality_disorders', label: 'Personality Disorders' },
  { value: 'substance_use', label: 'Substance Use' },
  { value: 'neurodevelopmental_disorders', label: 'Neurodevelopmental' },
  { value: 'trauma_and_stress', label: 'Trauma & Stress' },
  { value: 'eating_disorders', label: 'Eating Disorders' },
  { value: 'liaison_psychiatry', label: 'Liaison Psychiatry' },
  { value: 'child_and_adolescent', label: 'Child & Adolescent' },
  { value: 'older_adult', label: 'Older Adult' },
  { value: 'forensic', label: 'Forensic' },
  { value: 'specialist', label: 'Specialist' },
  { value: 'cross_cutting_skills', label: 'Cross-Cutting Skills' },
];

const DIFFICULTIES = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'expert', label: 'Expert' },
];

const SETTINGS = [
  { value: 'outpatient_clinic', label: 'Outpatient Clinic' },
  { value: 'inpatient_ward', label: 'Inpatient Ward' },
  { value: 'emergency_department', label: 'Emergency Department' },
  { value: 'community_mental_health', label: 'Community Mental Health' },
  { value: 'private_practice', label: 'Private Practice' },
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'school_counseling', label: 'School Counseling' },
];

// Assessments are now fetched dynamically from TestDefinitions via the API.
// The hardcoded list below is kept only as a fallback if the API call fails.
const FALLBACK_ASSESSMENTS = [
  'PHQ-9', 'GAD-7', 'PCL-5', 'AUDIT', 'DAST-10', 'MDQ',
  'ASRS', 'CAGE', 'EPDS', 'K10', 'DASS-21', 'BDI-II',
];

const DEFAULT_RUBRIC_DIMENSIONS = [
  { key: 'rapport_building', label: 'Rapport Building', weight: 15 },
  { key: 'assessment_skills', label: 'Assessment Skills', weight: 15 },
  { key: 'clinical_reasoning', label: 'Clinical Reasoning', weight: 15 },
  { key: 'therapeutic_techniques', label: 'Therapeutic Techniques', weight: 10 },
  { key: 'safety_management', label: 'Safety Management', weight: 15 },
  { key: 'communication_skills', label: 'Communication Skills', weight: 10 },
  { key: 'ethical_practice', label: 'Ethical Practice', weight: 10 },
  { key: 'documentation_formulation', label: 'Documentation & Formulation', weight: 10 },
];

const VISIBILITY_LEVELS = [
  { value: 'full_brief', label: 'Full Brief' },
  { value: 'referral_only', label: 'Referral Only' },
  { value: 'blind', label: 'Blind' },
];

const BLIND_MODE_ASSESSMENTS = [
  { value: 'full_list', label: 'Full List (student sees all assessments)' },
  { value: 'hidden', label: 'Hidden (no assessment button)' },
];

const emptyScenario = () => ({
  name: '',
  description: '',
  category: '',
  difficulty: 'intermediate',
  status: 'draft',
  institutionGroupId: '',
  visibilityLevel: 'full_brief',
  blindModeAssessments: 'full_list',
  referralSummary: '',
  scenarioContext: {
    setting: '',
    referralSource: '',
    presentingComplaint: '',
    backgroundBrief: '',
    timeLimit: 30,
  },
  patientSituation: { whereYouAre: '', recentEvents: '', howYouAreToday: '', withheldUnlessAskedWell: '' },
  learningObjectives: [''],
  availableAssessments: [],
  defaultMethodologyId: null,
  evaluationRubric: {
    dimensions: DEFAULT_RUBRIC_DIMENSIONS.map(d => ({ ...d })),
    passingScore: 60,
  },
  voiceConfig: {
    patientVoiceId: '',
    enableTTS: false,
    enableSTT: false,
    avmEnabled: false,
    avmVoicePreset: null,
    avmAccentPresetId: null,
    avmAccentGuidance: '',
  },
  curriculumMapping: [],
});

const ScenarioEditorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useTraining();
  const isAdmin = !!user?.role?.includes('admin');
  const location = useLocation();
  const isEditMode = Boolean(id);
  // Caller may pass `state: { from: '/admin/dashboard/training-scenarios' }`
  // so the back arrow returns to the page that launched the editor.
  // Falls back to the lecturer-facing scenario library when no source
  // is provided (e.g. direct link or page reload).
  const backTarget = location.state?.from || '/training/scenarios';

  const [scenario, setScenario] = useState(emptyScenario());
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Phase 3: persona library integration
  const [personaPickerOpen, setPersonaPickerOpen] = useState(false);
  const [customisingPersona, setCustomisingPersona] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    context: true,
    patient: false,
    objectives: true,
    assessments: false,
    rubric: false,
    voice: false,
    curriculum: false,
  });

  // Dynamic assessment definitions from the platform
  const [availableAssessmentDefs, setAvailableAssessmentDefs] = useState([]);
  // Evaluation methodologies for the default methodology picker
  const [methodologies, setMethodologies] = useState([]);
  // Service groups available to the user — used by the "Author for..." dropdown
  // so admins can scope a scenario to the platform library or a bespoke group.
  const [serviceGroups, setServiceGroups] = useState([]);
  const [platformGroupId, setPlatformGroupId] = useState(null);
  // Resolved platform default AVM voice id, surfaced in the picker so authors
  // can see what "Use platform default" actually maps to.
  const [platformDefaultAvmVoice, setPlatformDefaultAvmVoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    getPlatformSettings(token)
      .then(data => {
        if (cancelled) return;
        const v = data?.trainingVoiceConfig?.geminiLive?.voicePreset || null;
        setPlatformDefaultAvmVoice(v);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    trainingApi.getAvailableAssessments()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        const assessments = data.assessments || [];
        if (assessments.length > 0) {
          setAvailableAssessmentDefs(assessments.map(a => ({
            key: a.testKey,
            label: a.fullName || a.name || a.testKey,
          })));
        } else {
          // Fallback to hardcoded list
          setAvailableAssessmentDefs(FALLBACK_ASSESSMENTS.map(k => ({ key: k, label: k })));
        }
      })
      .catch(() => {
        // Fallback to hardcoded list on error
        setAvailableAssessmentDefs(FALLBACK_ASSESSMENTS.map(k => ({ key: k, label: k })));
      });
    // Fetch evaluation methodologies for the default methodology picker
    trainingApi.listMethodologies()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        const list = Array.isArray(data) ? data : (data.methodologies || []);
        setMethodologies(list);
      })
      .catch(() => {});
    // Groups this person can file the scenario in. Lecturers get the groups they teach
    // in (defaulting to theirs); admins get every group, defaulting to the Platform Library.
    trainingApi.getAuthorGroups()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setServiceGroups(Array.isArray(data.groups) ? data.groups : []);
        setPlatformGroupId(data.libraryGroupId || null);
        if (data.defaultGroupId && !isEditMode) {
          setScenario(prev => prev.institutionGroupId
            ? prev
            : { ...prev, institutionGroupId: data.defaultGroupId });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEditMode]);

  // Load scenario for edit mode
  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await trainingApi.getScenario(id);
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        const s = data.scenario || data;

        // Merge loaded data with defaults to ensure all fields exist
        // Backend uses 'evaluationConfig', editor uses 'evaluationRubric' — map on load
        const evalConfig = s.evaluationRubric || s.evaluationConfig || {};
        setScenario({
          ...emptyScenario(),
          ...s,
          scenarioContext: { ...emptyScenario().scenarioContext, ...(s.scenarioContext || {}) },
          patientSituation: { ...emptyScenario().patientSituation, ...(s.patientSituation || {}) },
          evaluationRubric: {
            dimensions: evalConfig.dimensions?.length
              ? evalConfig.dimensions
              : DEFAULT_RUBRIC_DIMENSIONS.map(d => ({ ...d })),
            passingScore: evalConfig.passingScore ?? 60,
          },
          voiceConfig: { ...emptyScenario().voiceConfig, ...(s.voiceConfig || {}) },
          learningObjectives: s.learningObjectives?.length ? s.learningObjectives : [''],
          curriculumMapping: s.curriculumMappings || s.curriculumMapping || [],
        });
      } catch (err) {
        if (!cancelled) {
          console.error('[ScenarioEditor] Load error:', err);
          setError(err.response?.data?.error || err.message || 'Failed to load scenario');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id, isEditMode]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Persona library integration (Phase 3) ───────────────────────────────
  // Silent fork: clone the linked persona into the scenario's org and rewire
  // the scenario to the new copy. After this, edits in the persona editor
  // affect only this scenario's fork — no further clones are created.
  const [personaEditOpen, setPersonaEditOpen] = useState(false);
  const handleCustomisePersona = async () => {
    if (!isEditMode || !scenario._id) {
      setError('Save the scenario first, then customise its persona.');
      return;
    }
    const confirmMsg = (
      'This will create a private copy of the persona for this scenario. ' +
      'The original in the library is unchanged, and any future edits stay ' +
      'within this copy. Continue?'
    );
    if (!window.confirm(confirmMsg)) return;
    setCustomisingPersona(true);
    setError(null);
    try {
      const result = await customiseScenarioPersona(scenario._id, {}, null);
      setScenario(prev => ({ ...prev, clientPersonaId: result.persona }));
      setSuccess(`Persona customised. This scenario now uses "${result.persona.name}".`);
    } catch (err) {
      setError(err?.msg || err?.message || 'Failed to customise persona');
    } finally {
      setCustomisingPersona(false);
    }
  };

  // Replace: swap the scenario's persona reference to a different library
  // persona. Persisted on next Save.
  const handleReplacePersona = (newPersona) => {
    setScenario(prev => ({ ...prev, clientPersonaId: newPersona }));
    setPersonaPickerOpen(false);
    setSuccess(`Persona changed to "${newPersona.name}". Remember to Save to persist.`);
  };

  const updateField = (path, value) => {
    setScenario(prev => {
      const parts = path.split('.');
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  // A scenario this person can open but not change (a Platform Library scenario, or
  // another group's): they copy it into their own group and edit the copy.
  const readOnly = isEditMode && scenario.canEdit === false;
  const [isCopying, setIsCopying] = useState(false);
  const handleCopyToMyGroup = async () => {
    setIsCopying(true);
    setError(null);
    try {
      const response = await trainingApi.copyScenario(id);
      const body = response.data || response;
      const copy = body.data || body;
      navigate(`/training/scenarios/${copy._id}/edit`);
      setSuccess('Copied into your group. It is hidden from trainees until you make it available.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not copy this scenario');
    } finally {
      setIsCopying(false);
    }
  };

  // Admins only (Dave, 5 Oct 2026): put a copy of a group's scenario in the Platform
  // Library so every training group can use it.
  const scenarioGroupId = typeof scenario.institutionGroupId === 'object' && scenario.institutionGroupId !== null
    ? scenario.institutionGroupId._id
    : scenario.institutionGroupId;
  const canAddToLibrary = isAdmin && isEditMode && platformGroupId && scenarioGroupId && scenarioGroupId !== platformGroupId;
  const handleAddToLibrary = async () => {
    setIsCopying(true);
    setError(null);
    try {
      const response = await trainingApi.copyScenario(id, platformGroupId);
      const body = response.data || response;
      const copy = body.data || body;
      navigate(`/training/scenarios/${copy._id}/edit`);
      setSuccess('Added to the Platform Library. Lecturers in every training group can now copy it.');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not add this scenario to the Platform Library');
    } finally {
      setIsCopying(false);
    }
  };

  const handleSave = async (publishAfterSave = false) => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Clean up empty objectives and map field names for backend
      const { evaluationRubric, curriculumMapping, ...rest } = scenario;
      const payload = {
        ...rest,
        learningObjectives: scenario.learningObjectives.filter(o => o.trim()),
        // Backend uses 'evaluationConfig' not 'evaluationRubric'
        evaluationConfig: evaluationRubric,
        // Backend uses 'curriculumMappings' not 'curriculumMapping'
        curriculumMappings: curriculumMapping,
      };

      if (publishAfterSave) {
        payload.status = 'published';
      }

      let response;
      if (isEditMode) {
        response = await trainingApi.updateScenario(id, payload);
      } else {
        response = await trainingApi.createScenario(payload);
      }

      const body = response.data || response;
      const data = body.data || body;
      const saved = data.scenario || data;

      if (!isEditMode && saved._id) {
        // Navigate to edit mode for the newly created scenario
        navigate(`/training/scenarios/${saved._id}/edit`, { replace: true });
      }

      setSuccess(publishAfterSave ? 'Scenario published successfully.' : 'Scenario saved successfully.');
    } catch (err) {
      console.error('[ScenarioEditor] Save error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save scenario');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await trainingApi.deleteScenario(id);
      navigate(backTarget);
    } catch (err) {
      console.error('[ScenarioEditor] Delete error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete scenario');
    }
    setShowDeleteDialog(false);
  };

  // Objective helpers
  const addObjective = () => {
    setScenario(prev => ({
      ...prev,
      learningObjectives: [...prev.learningObjectives, ''],
    }));
  };

  const removeObjective = (idx) => {
    setScenario(prev => ({
      ...prev,
      learningObjectives: prev.learningObjectives.filter((_, i) => i !== idx),
    }));
  };

  const updateObjective = (idx, value) => {
    setScenario(prev => ({
      ...prev,
      learningObjectives: prev.learningObjectives.map((o, i) => i === idx ? value : o),
    }));
  };

  // Assessment toggle
  const toggleAssessment = (key) => {
    setScenario(prev => {
      const current = prev.availableAssessments || [];
      const next = current.includes(key)
        ? current.filter(a => a !== key)
        : [...current, key];
      return { ...prev, availableAssessments: next };
    });
  };

  // Rubric dimension weight update
  const updateDimensionWeight = (idx, weight) => {
    setScenario(prev => ({
      ...prev,
      evaluationRubric: {
        ...prev.evaluationRubric,
        dimensions: prev.evaluationRubric.dimensions.map((d, i) =>
          i === idx ? { ...d, weight } : d
        ),
      },
    }));
  };

  // Curriculum mapping helpers
  const addCurriculumMapping = () => {
    setScenario(prev => ({
      ...prev,
      curriculumMapping: [...prev.curriculumMapping, { framework: '', domain: '', competencies: '' }],
    }));
  };

  const removeCurriculumMapping = (idx) => {
    setScenario(prev => ({
      ...prev,
      curriculumMapping: prev.curriculumMapping.filter((_, i) => i !== idx),
    }));
  };

  const updateCurriculumMapping = (idx, field, value) => {
    setScenario(prev => ({
      ...prev,
      curriculumMapping: prev.curriculumMapping.map((m, i) =>
        i === idx ? { ...m, [field]: value } : m
      ),
    }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(backTarget)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {isEditMode ? 'Edit Scenario' : 'Create New Scenario'}
        </Typography>
        {scenario.status && (
          <Chip
            label={scenario.status}
            size="small"
            color={scenario.status === 'published' ? 'success' : 'default'}
            sx={{ textTransform: 'capitalize', ml: 1 }}
          />
        )}
        {platformGroupId && (
          (scenario.institutionGroupId === platformGroupId
            || (typeof scenario.institutionGroupId === 'object'
              && scenario.institutionGroupId?._id === platformGroupId))
        ) && (
          <Chip
            label="Platform Library"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>
      )}
      {readOnly && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" onClick={handleCopyToMyGroup} disabled={isCopying}>
              {isCopying ? 'Copying...' : 'Use in my group'}
            </Button>
          )}
        >
          {scenario.fromLibrary
            ? 'This is a Platform Library scenario. Use it in your group to get your own copy you can change and give to trainees.'
            : 'This scenario belongs to another group. Use it in your group to get your own copy.'}
        </Alert>
      )}

      {/* 1. Basic Info */}
      <Accordion expanded={expandedSections.basic} onChange={() => toggleSection('basic')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Basic Info</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Scenario Name"
              fullWidth
              value={scenario.name}
              onChange={(e) => updateField('name', e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={scenario.description}
              onChange={(e) => updateField('description', e.target.value)}
            />
            {serviceGroups.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Author for...</InputLabel>
                <Select
                  value={
                    typeof scenario.institutionGroupId === 'object' && scenario.institutionGroupId !== null
                      ? (scenario.institutionGroupId._id || '')
                      : (scenario.institutionGroupId || '')
                  }
                  label="Author for..."
                  onChange={(e) => updateField('institutionGroupId', e.target.value)}
                >
                  {serviceGroups
                    .slice()
                    .sort((a, b) => {
                      // Platform group first, then alphabetical
                      if (a.groupType === 'platform_authoring') return -1;
                      if (b.groupType === 'platform_authoring') return 1;
                      return (a.name || '').localeCompare(b.name || '');
                    })
                    .map(g => (
                      <MenuItem key={g._id} value={g._id}>
                        {g.name}
                        {g.groupType === 'platform_authoring' && ' (Platform Library)'}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel
              control={(
                <Switch
                  checked={scenario.studentAccess === 'library'}
                  disabled={readOnly}
                  onChange={(e) => updateField('studentAccess', e.target.checked ? 'library' : 'hidden')}
                />
              )}
              label={scenario.studentAccess === 'assignment_only'
                ? 'Trainees get this through assignments only'
                : 'Available to trainees for practice'}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={scenario.category}
                  label="Category"
                  onChange={(e) => updateField('category', e.target.value)}
                >
                  {CATEGORIES.map(c => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={scenario.difficulty}
                  label="Difficulty"
                  onChange={(e) => updateField('difficulty', e.target.value)}
                >
                  {DIFFICULTIES.map(d => (
                    <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Visibility Configuration */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Student Visibility
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Visibility Level</InputLabel>
                <Select
                  value={scenario.visibilityLevel || 'full_brief'}
                  label="Visibility Level"
                  onChange={(e) => updateField('visibilityLevel', e.target.value)}
                >
                  {VISIBILITY_LEVELS.map(v => (
                    <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {scenario.visibilityLevel === 'blind' && (
                <FormControl fullWidth>
                  <InputLabel>Blind Mode Assessments</InputLabel>
                  <Select
                    value={scenario.blindModeAssessments || 'full_list'}
                    label="Blind Mode Assessments"
                    onChange={(e) => updateField('blindModeAssessments', e.target.value)}
                  >
                    {BLIND_MODE_ASSESSMENTS.map(b => (
                      <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            {(scenario.visibilityLevel === 'referral_only' || scenario.visibilityLevel === 'blind') && (
              <TextField
                label="Referral Summary"
                fullWidth
                placeholder='e.g., "GP referral: persistent low mood, 3 months duration"'
                value={scenario.referralSummary || ''}
                onChange={(e) => updateField('referralSummary', e.target.value)}
                helperText="Short one-line referral shown to students in referral_only mode. Also used as fallback in blind mode if referral info is needed."
              />
            )}
            {/* Student access is controlled by Training Admin, not in the scenario editor */}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 2. Scenario Context */}
      <Accordion expanded={expandedSections.context} onChange={() => toggleSection('context')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Scenario Context</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Setting</InputLabel>
                <Select
                  value={scenario.scenarioContext.setting}
                  label="Setting"
                  onChange={(e) => updateField('scenarioContext.setting', e.target.value)}
                >
                  {SETTINGS.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Referral Source"
                fullWidth
                value={scenario.scenarioContext.referralSource}
                onChange={(e) => updateField('scenarioContext.referralSource', e.target.value)}
              />
            </Box>
            <TextField
              label="Presenting Complaint"
              fullWidth
              multiline
              rows={2}
              value={scenario.scenarioContext.presentingComplaint}
              onChange={(e) => updateField('scenarioContext.presentingComplaint', e.target.value)}
            />
            <TextField
              label="Background Brief"
              fullWidth
              multiline
              rows={3}
              value={scenario.scenarioContext.backgroundBrief}
              onChange={(e) => updateField('scenarioContext.backgroundBrief', e.target.value)}
            />
            <TextField
              label="Time Limit (minutes)"
              type="number"
              value={scenario.scenarioContext.timeLimit}
              onChange={(e) => updateField('scenarioContext.timeLimit', parseInt(e.target.value) || 0)}
              sx={{ maxWidth: 200 }}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 3. Patient Profile — library-driven panel (Phase 3) */}
      <Accordion expanded={expandedSections.patient} onChange={() => toggleSection('patient')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Patient Profile</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PersonaPickerModal
            open={personaPickerOpen}
            onClose={() => setPersonaPickerOpen(false)}
            onSelect={handleReplacePersona}
            token={null}
            preselectedKey={typeof scenario.clientPersonaId === 'object' ? scenario.clientPersonaId?.key : null}
            apiScope="training"
            title="Replace patient persona"
          />
          <PersonaEditDialog
            open={personaEditOpen}
            personaId={typeof scenario.clientPersonaId === 'object' ? scenario.clientPersonaId?._id : scenario.clientPersonaId}
            onClose={() => setPersonaEditOpen(false)}
            onSaved={(saved) => {
              setScenario(prev => ({
                ...prev,
                clientPersonaId: typeof prev.clientPersonaId === 'object' && prev.clientPersonaId
                  ? { ...prev.clientPersonaId, name: saved.name, description: saved.description, metadata: saved.metadata }
                  : prev.clientPersonaId,
              }));
              setPersonaEditOpen(false);
              setSuccess('Persona saved.');
            }}
          />

          {scenario.clientPersonaId ? (
            /* clientPersonaId may be a string ID or a populated object */
            (() => {
              const persona = typeof scenario.clientPersonaId === 'object' ? scenario.clientPersonaId : null;
              const conditions = persona?.metadata?.conditions || [];
              const demo = persona?.metadata?.demographics || {};
              const lineageSource = persona?.lineage?.clonedFrom;
              const isCustomisedFork = !!lineageSource;

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Lineage breadcrumb — keeps the silent fork visible */}
                  {isCustomisedFork && (
                    <Alert severity="info" icon={<AccountTreeIcon />} sx={{ py: 0.5 }}>
                      Customised from <strong>{typeof lineageSource === 'object' ? (lineageSource.name || lineageSource.key) : 'another persona'}</strong>
                      {persona?.lineage?.clonedAt && ` · ${new Date(persona.lineage.clonedAt).toLocaleDateString()}`}
                      . Edits to it affect only scenarios using this copy.
                    </Alert>
                  )}

                  {/* Persona summary card */}
                  <Card variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar
                      src={persona?.personaImage?.url || undefined}
                      sx={{ width: 72, height: 72 }}
                    >
                      {persona?.name?.charAt(0) || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" noWrap>
                        {persona?.name || (typeof scenario.clientPersonaId === 'string' ? scenario.clientPersonaId : 'Unknown persona')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {persona?.key}
                      </Typography>
                      {conditions.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {conditions.slice(0, 5).map(c => (
                            <Chip key={c} label={c} size="small" variant="outlined" />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {demo.ageRange && <Chip size="small" label={demo.ageRange} />}
                        {demo.gender && <Chip size="small" label={demo.gender} />}
                        {demo.culturalContextSummary && <Chip size="small" label={demo.culturalContextSummary} variant="outlined" />}
                        {persona?.metadata?.severity && <Chip size="small" label={persona.metadata.severity} color="warning" variant="outlined" />}
                      </Box>
                    </Box>
                  </Card>

                  {/* Action row */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {isAdmin && persona?.key && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        href={`/admin/dashboard/persona-library?focus=${encodeURIComponent(persona.key)}`}
                        target="_blank"
                      >
                        View in library
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant={isCustomisedFork ? 'outlined' : 'contained'}
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCustomisePersona}
                      disabled={customisingPersona || !isEditMode || isCustomisedFork || readOnly}
                    >
                      {isCustomisedFork ? 'Already customised for this scenario' : (customisingPersona ? 'Customising…' : 'Customise for this scenario')}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={() => setPersonaPickerOpen(true)}
                      disabled={readOnly}
                    >
                      Replace from library
                    </Button>
                    {isEditMode && !readOnly && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setPersonaEditOpen(true)}
                      >
                        Edit persona
                      </Button>
                    )}
                    {isAdmin && persona?.key && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<EditIcon />}
                        href={`/admin/dashboard/client-editor?key=${encodeURIComponent(persona.key)}`}
                        target="_blank"
                      >
                        Open in full persona editor
                      </Button>
                    )}
                  </Box>

                  {!isEditMode && (
                    <Typography variant="caption" color="text.secondary">
                      Save the scenario first to enable Customise (it needs a stable scenario ID to atomically rewire the fork).
                    </Typography>
                  )}

                  {/* Persona image generation (unchanged from previous behaviour) */}
                  <PersonaImageSection
                    scenario={scenario}
                    isEditMode={isEditMode}
                    onImageChange={(personaImage) => setScenario(prev => ({
                      ...prev,
                      clientPersonaId: typeof prev.clientPersonaId === 'object'
                        ? { ...prev.clientPersonaId, personaImage }
                        : prev.clientPersonaId,
                    }))}
                    setError={setError}
                  />
                </Box>
              );
            })()
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No patient persona linked yet.
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => setPersonaPickerOpen(true)}
              >
                Choose from library
              </Button>
            </Box>
          )}
          <PatientSituationPanel
            value={scenario.patientSituation}
            onChange={(next) => setScenario(prev => ({ ...prev, patientSituation: next }))}
            scenario={scenario}
            readOnly={readOnly}
          />
        </AccordionDetails>
      </Accordion>

      {/* 4. Learning Objectives */}
      <Accordion expanded={expandedSections.objectives} onChange={() => toggleSection('objectives')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Learning Objectives ({scenario.learningObjectives.filter(o => o.trim()).length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {scenario.learningObjectives.map((obj, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Objective ${idx + 1}`}
                  value={obj}
                  onChange={(e) => updateObjective(idx, e.target.value)}
                />
                <IconButton
                  size="small"
                  onClick={() => removeObjective(idx)}
                  disabled={scenario.learningObjectives.length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addObjective}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Objective
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 5. Available Assessments */}
      <Accordion expanded={expandedSections.assessments} onChange={() => toggleSection('assessments')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Available Assessments ({(scenario.availableAssessments || []).length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Warn (not block) on assessment keys saved on the scenario that
              are not in the platform's TestDefinition catalogue. The
              scenario remains valid; admins can either add the missing
              TestDefinition to the platform or remove it from the
              scenario's selection. Loading state is suppressed by checking
              the def list is non-empty. */}
          {(() => {
            if (availableAssessmentDefs.length === 0) return null;
            const known = new Set(availableAssessmentDefs.map(d => d.key));
            const missing = (scenario.availableAssessments || []).filter(k => !known.has(k));
            if (missing.length === 0) return null;
            return (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{missing.length === 1 ? 'Missing assessment' : 'Missing assessments'}:</strong>{' '}
                {missing.join(', ')} — not present in the platform catalogue (or disabled). Therapists
                running this scenario won't see {missing.length === 1 ? 'it' : 'them'} in the
                in-session assessment list. Ask an admin to enable the
                {missing.length === 1 ? ' TestDefinition' : ' TestDefinitions'}, or remove
                {missing.length === 1 ? ' it' : ' them'} from the selection below.
              </Alert>
            );
          })()}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {availableAssessmentDefs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Loading assessments...</Typography>
            ) : (
              <>
                {availableAssessmentDefs.map(({ key, label }) => {
                  const selected = (scenario.availableAssessments || []).includes(key);
                  return (
                    <Chip
                      key={key}
                      label={label}
                      clickable
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleAssessment(key)}
                    />
                  );
                })}
                {/* Render the missing keys as outlined warning chips so the
                    author can untick them without having to edit JSON. */}
                {(() => {
                  const known = new Set(availableAssessmentDefs.map(d => d.key));
                  return (scenario.availableAssessments || [])
                    .filter(k => !known.has(k))
                    .map(missingKey => (
                      <Chip
                        key={`missing-${missingKey}`}
                        label={`${missingKey} (missing)`}
                        clickable
                        color="warning"
                        variant="outlined"
                        onClick={() => toggleAssessment(missingKey)}
                        sx={{ borderStyle: 'dashed' }}
                      />
                    ));
                })()}
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 6. Evaluation Rubric */}
      <Accordion expanded={expandedSections.rubric} onChange={() => toggleSection('rubric')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Evaluation Rubric</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Default Evaluation Methodology Picker */}
            <FormControl fullWidth>
              <InputLabel>Default Evaluation Methodology</InputLabel>
              <Select
                value={scenario.defaultMethodologyId || ''}
                label="Default Evaluation Methodology"
                onChange={(e) => updateField('defaultMethodologyId', e.target.value || null)}
              >
                <MenuItem value="">
                  <em>None (use global active methodology or scenario rubric)</em>
                </MenuItem>
                {methodologies.map(m => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.name}{m.organisation ? ` (${m.organisation})` : ''} — v{m.version || 1}
                    {m.isActive ? ' [active]' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {scenario.defaultMethodologyId && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                This methodology will be used by default when evaluating completed sessions for this scenario, overriding the global active methodology.
              </Alert>
            )}

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">
              Fallback Rubric Dimensions
            </Typography>
            <Typography variant="caption" color="text.secondary">
              These dimension weights are used only if no evaluation methodology is selected above and no global methodology is active.
            </Typography>
            {scenario.evaluationRubric.dimensions.map((dim, idx) => (
              <Box key={dim.key || idx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={500}>
                    {dim.label || dim.key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dim.weight}%
                  </Typography>
                </Box>
                <Slider
                  value={dim.weight}
                  min={0}
                  max={50}
                  step={5}
                  onChange={(_, val) => updateDimensionWeight(idx, val)}
                  size="small"
                />
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total weight: {scenario.evaluationRubric.dimensions.reduce((sum, d) => sum + d.weight, 0)}%
              </Typography>
              <TextField
                label="Passing Score"
                type="number"
                size="small"
                value={scenario.evaluationRubric.passingScore}
                onChange={(e) => updateField('evaluationRubric.passingScore', parseInt(e.target.value) || 0)}
                sx={{ width: 130 }}
              />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 7. Voice Config */}
      <Accordion expanded={expandedSections.voice} onChange={() => toggleSection('voice')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>Voice Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* ElevenLabs TTS (existing) — used in Chat / Voice modes
                where AI replies are played via on-demand or auto-play TTS.
                NOT used by Advanced Voice Mode (which has its own Gemini
                voice selector below). */}
            <Typography variant="subtitle2">Text-to-Speech Voice (used in Chat &amp; Voice modes)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              ElevenLabs voice that plays AI replies when the user clicks the message speaker, or
              auto-plays them in Voice mode. Not used by Call mode — that uses a separate live voice below.
            </Typography>
            <VoiceSelector
              value={scenario.voiceConfig.patientVoiceId}
              onChange={(voiceId) => updateField('voiceConfig.patientVoiceId', voiceId)}
              label="ElevenLabs TTS Voice"
              sampleText={scenario.scenarioContext?.presentingComplaint || undefined}
            />
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={scenario.voiceConfig.enableTTS}
                    onChange={(e) => updateField('voiceConfig.enableTTS', e.target.checked)}
                  />
                }
                label="Enable Text-to-Speech"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={scenario.voiceConfig.enableSTT}
                    onChange={(e) => updateField('voiceConfig.enableSTT', e.target.checked)}
                  />
                }
                label="Enable Speech-to-Text"
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Advanced Voice Mode (Gemini Live) */}
            <Typography variant="subtitle2">Advanced Voice Mode (Live Conversation)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Enable real-time spoken conversation between the student and the simulated patient.
              The patient AI will speak using the selected Gemini voice.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={scenario.voiceConfig.avmEnabled || false}
                  onChange={(e) => updateField('voiceConfig.avmEnabled', e.target.checked)}
                  color="secondary"
                />
              }
              label={scenario.voiceConfig.avmEnabled ? 'Advanced Voice Mode: ON' : 'Advanced Voice Mode: OFF'}
            />

            {scenario.voiceConfig.avmEnabled && (
              <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'secondary.light', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" fontWeight={500}>Accent / Regional Style</Typography>
                <AccentSelector
                  value={scenario.voiceConfig.avmAccentPresetId || null}
                  onChange={(presetId) => updateField('voiceConfig.avmAccentPresetId', presetId)}
                  customGuidance={scenario.voiceConfig.avmAccentGuidance || ''}
                  onCustomGuidanceChange={(text) => updateField('voiceConfig.avmAccentGuidance', text)}
                  token={localStorage.getItem('token')}
                  onGenerate={isEditMode ? async () => {
                    const response = await trainingApi.generateAvmVoiceGuidance(scenario._id);
                    const body = response?.data || response;
                    const data = body?.data || body;
                    return data?.guidance || '';
                  } : undefined}
                />

                <Typography variant="body2" fontWeight={500} sx={{ mt: 1 }}>Live Conversation Voice (Gemini)</Typography>
                <Typography variant="caption" color="text.secondary">
                  This is the voice the patient speaks with in <strong>Call mode</strong> — it is independent
                  of the ElevenLabs TTS voice above. Leave as "Use platform default" to inherit from the
                  admin training voice settings, or pick a specific voice (e.g. Aoede / Kore for female,
                  Charon / Orus for male).
                </Typography>
                <GeminiVoiceSelector
                  value={scenario.voiceConfig.avmVoicePreset}
                  onChange={(voice) => updateField('voiceConfig.avmVoicePreset', voice)}
                  token={localStorage.getItem('token')}
                  showPlatformDefault
                  platformDefaultVoiceId={platformDefaultAvmVoice}
                  accentPresetId={scenario.voiceConfig.avmAccentPresetId}
                />
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* 8. Curriculum Mapping */}
      <Accordion expanded={expandedSections.curriculum} onChange={() => toggleSection('curriculum')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={600}>
            Curriculum Mapping ({scenario.curriculumMapping.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {scenario.curriculumMapping.map((mapping, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" fontWeight={500}>Mapping {idx + 1}</Typography>
                  <IconButton size="small" onClick={() => removeCurriculumMapping(idx)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Framework"
                    size="small"
                    fullWidth
                    value={mapping.framework}
                    onChange={(e) => updateCurriculumMapping(idx, 'framework', e.target.value)}
                  />
                  <TextField
                    label="Domain"
                    size="small"
                    fullWidth
                    value={mapping.domain}
                    onChange={(e) => updateCurriculumMapping(idx, 'domain', e.target.value)}
                  />
                  <TextField
                    label="Competencies (comma-separated)"
                    size="small"
                    fullWidth
                    value={Array.isArray(mapping.competencies) ? mapping.competencies.join(', ') : (mapping.competencies || '')}
                    onChange={(e) => updateCurriculumMapping(idx, 'competencies', e.target.value)}
                  />
                </Box>
              </Paper>
            ))}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addCurriculumMapping}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Curriculum Mapping
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {readOnly ? (
            <Button variant="contained" onClick={handleCopyToMyGroup} disabled={isCopying}>
              {isCopying ? 'Copying...' : 'Use in my group'}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={() => handleSave(false)}
                disabled={isSaving || !scenario.name.trim()}
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<PublishIcon />}
                onClick={() => handleSave(true)}
                disabled={isSaving || !scenario.name.trim()}
              >
                Publish
              </Button>
            </>
          )}
          {canAddToLibrary && (
            <Button variant="outlined" onClick={handleAddToLibrary} disabled={isCopying}>
              Add to Platform Library
            </Button>
          )}
          {isEditMode && (
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(true)}
            >
              Preview
            </Button>
          )}
        </Box>
        <Box>
          {isEditMode && !readOnly && scenario.status === 'draft' && (
            <Button
              color="error"
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete
            </Button>
          )}
        </Box>
      </Paper>

      {/* Preview Dialog */}
      {isEditMode && (
        <ScenarioPreviewDialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          scenarioId={id}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Scenario?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{scenario.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScenarioEditorPage;
